import { Dayjs } from 'dayjs'
import { GetInboundsReq } from '@/services/inbound/inboundInquiry/types'
import { DATE_FORMAT, FIELD_NAMES, ORDER_NUMBER_LIMIT, ORD_OPTION_NUMBER_LIMIT } from './constants'

/** FilterArea 날짜 필드 값 — 단일/기간 어느 쪽으로 오든 받는다. */
type DateValue = Dayjs | [Dayjs | null, Dayjs | null] | null | undefined

export interface InboundInquiryFormValues {
  /** pbo-core `logistics.ShipperSelect` — `typeMode` 기본값(멀티)이라 배열. */
  [FIELD_NAMES.serviceTypes]?: string[]
  /**
   * 화주명 칸의 값 — 컨덕터 뷰의 화주명 칸이 다중 선택이라 항상 배열로 온다.
   * BE `shipperCode` 는 단일값만 받으므로 `toInboundsReq` 가 첫 값만 쓰고,
   * 2개 이상이면 `hasTooManyShipperNames` 가 조회 전에 막는다.
   */
  [FIELD_NAMES.shipperCode]?: string[]
  [FIELD_NAMES.plnDateRange]?: DateValue
  /** `mfs.FormTextArea` 값 — 배열로 오지만 단건 문자열로 올 때도 받는다. */
  [FIELD_NAMES.ordOptionNumbers]?: string[] | string
  /** 대표입고번호 — `mfs.FormTextArea` 값. 입고번호와 같은 다중 입력이다. */
  [FIELD_NAMES.orderNumbers]?: string[] | string
  [FIELD_NAMES.orderType]?: string
  [FIELD_NAMES.actDate]?: DateValue
  [FIELD_NAMES.warehouseName]?: string
}

const toDateString = (value?: Dayjs | null): string | undefined => {
  if (!value?.isValid()) return undefined
  return value.format(DATE_FORMAT)
}

/** 기간 필드에서 [시작, 끝] 을 꺼낸다. 단일 값으로 오면 시작·끝을 같은 날로 본다. */
const toRange = (value: DateValue): [Dayjs | null, Dayjs | null] => {
  if (!value) return [null, null]
  if (Array.isArray(value)) return [value[0] ?? null, value[1] ?? null]
  return [value, value]
}

/** 단일 날짜 필드에서 한 날을 꺼낸다. 기간으로 그려져 값이 배열로 와도 시작일을 쓴다. */
const toSingleDate = (value: DateValue): Dayjs | null => {
  if (!value) return null
  return Array.isArray(value) ? (value[0] ?? null) : value
}

/** 다건 입력·멀티셀렉트 값은 배열로도 단건 문자열로도 온다 — 양쪽 다 목록으로 맞춘다(문자열만 받고 빈 값은 거른다). 엔트리(index.tsx)도 같은 규칙을 쓴다. */
export const toTrimmedList = (values: unknown): string[] => {
  if (Array.isArray(values)) return values.map((v) => String(v ?? '').trim()).filter(Boolean)
  if (typeof values === 'string') return values.trim() ? [values.trim()] : []
  return []
}

/** 조회 요청 변환 옵션 — 지금은 대표입고번호 FF 하나다. */
export interface ToInboundsReqOptions {
  /**
   * 대표입고번호 필터 노출 여부(`INBOUND_INQUIRY_ORDER_NO_FILTER_FLAG`). 기본값은 꺼짐이다 —
   * 옵션을 빠뜨린 호출부가 플래그를 우회해 `orderNumbers` 를 실어 보내지 않게 fail-closed 로 둔다.
   */
  isOrderNoFilterEnabled?: boolean
}

/**
 * 필터 폼 값 → 조회 요청 변환.
 *
 * 빈 값은 싣지 않는다 — 대부분의 필터가 완전일치라 빈 문자열이 그대로 가면 무결과 또는 400 이 된다.
 * 날짜는 `YYYY-MM-DD` 로 보낸다(3.1 참조).
 *
 * 대표입고번호는 플래그가 켜졌을 때만 싣는다. 화면에서 칸을 감추는 것만으로는 부족하다 —
 * 플래그를 껐을 때 URL·세션에 남아 있던 옛 값이 안 보이는 필터로 걸릴 수 있다.
 */
export const toInboundsReq = (
  values: InboundInquiryFormValues,
  { isOrderNoFilterEnabled = false }: ToInboundsReqOptions = {},
): Omit<GetInboundsReq, 'page' | 'size'> => {
  const serviceTypes = toTrimmedList(values[FIELD_NAMES.serviceTypes])
  // BE 는 단일값만 받는다 — 화면이 2개 이상 선택을 막으므로(hasTooManyShipperNames) 여기서는 첫 값만 쓴다.
  const shipperCode = toTrimmedList(values[FIELD_NAMES.shipperCode])[0]
  const ordOptionNumbers = toTrimmedList(values[FIELD_NAMES.ordOptionNumbers])
  const orderNumbers = isOrderNoFilterEnabled ? toTrimmedList(values[FIELD_NAMES.orderNumbers]) : []
  const [plnFrom, plnTo] = toRange(values[FIELD_NAMES.plnDateRange])
  const plnDateFrom = toDateString(plnFrom)
  const plnDateTo = toDateString(plnTo)
  const actDate = toDateString(toSingleDate(values[FIELD_NAMES.actDate]))

  return {
    ...(shipperCode ? { shipperCode } : {}),
    ...(serviceTypes.length > 0 ? { serviceTypes } : {}),
    ...(ordOptionNumbers.length > 0 ? { ordOptionNumbers } : {}),
    ...(orderNumbers.length > 0 ? { orderNumbers } : {}),
    ...(values[FIELD_NAMES.orderType] ? { orderType: values[FIELD_NAMES.orderType] } : {}),
    ...(values[FIELD_NAMES.warehouseName] ? { warehouseName: values[FIELD_NAMES.warehouseName] } : {}),
    ...(plnDateFrom ? { plnDateFrom } : {}),
    ...(plnDateTo ? { plnDateTo } : {}),
    ...(actDate ? { actDate } : {}),
  }
}

/**
 * 필수 필터 검증 — 화주(유형 또는 명) AND 입고예정일자(7.3 노트). BE 서버 검증이 없어 화면이 막는다.
 * 화주는 GR05 와 같은 OR 조건이지만, 입고예정일자는 화면이 추가로 요구한다(디자인 필수 표시).
 */
export const hasRequiredFilter = (req: Omit<GetInboundsReq, 'page' | 'size'>): boolean =>
  ((req.shipperCode?.length ?? 0) > 0 || (req.serviceTypes?.length ?? 0) > 0) && !!req.plnDateFrom && !!req.plnDateTo

/** 입고번호가 BE IN 상한(200개)을 넘는가. */
export const exceedsOrdOptionNumberLimit = (req: Omit<GetInboundsReq, 'page' | 'size'>): boolean =>
  (req.ordOptionNumbers?.length ?? 0) > ORD_OPTION_NUMBER_LIMIT

/**
 * 대표입고번호가 BE IN 상한(200개)을 넘는가 — `GetInboundsRequest.orderNumbers` 도 `@Size(max = 200)` 이다.
 * FF 대기 코드 — 대표입고번호 칸은 컨덕터 데모 조회영역에 없어(2026-09-28 재핸드오프) 엔트리가 지금은 부르지 않는다. 칸이 데모에 실리면 `toInboundsReq` 옵션과 함께 살린다.
 */
export const exceedsOrderNumberLimit = (req: Omit<GetInboundsReq, 'page' | 'size'>): boolean =>
  (req.orderNumbers?.length ?? 0) > ORDER_NUMBER_LIMIT

/**
 * 화주명을 2개 이상 골랐는가 — BE `shipperCode` 는 단일값만 받는다.
 * 뷰의 화주명 칸이 다중 선택으로 그려지므로 조회 전에 이 값으로 막는다.
 */
export const hasTooManyShipperNames = (values: InboundInquiryFormValues): boolean =>
  toTrimmedList(values[FIELD_NAMES.shipperCode]).length > 1

/**
 * 다운로드 사유 모달 입력 → 감사용 사유 문자열. 1.0 RemarkPop `REMARK`(`{사유코드}_{상세사유}`) 동치 —
 * BE `AccessAuditAspect` 가 `detailedReason` 요청 파라미터를 EXPORT 감사로그 remark 에 그대로 남긴다.
 */
export const toDetailedReason = ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string }): string =>
  `${reasonCode}_${reasonDetail}`
